import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import message from 'antd/lib/message'
import get from 'lodash/get'

import { analytics, dateFormatter, form, strings } from 'helpers'
import { objectDiff } from 'helpers/utils'
import { useAuthState, useUpdateUser } from 'hooks'
import { BIRTHDATE_FORMAT, OTHER_GENDER } from 'constants/user'
import { MIN_NUM_OF_IMAGES, PROFILE_STEPS as STEPS } from 'constants/auth'
import { PROFILE_CONTAINER } from '../styles'

import { TimesThinIcon, UsernameInput } from 'components'

import DefaultInput from '../components/Input'
import CloseButton from '../components/CloseButton'
import Description from '../components/Description'
import BirthdateInput from 'components/BirthdateInput'

import Label from 'components/AuthModal/components/Label'
import Button from 'components/AuthModal/components/Button'
import MotionContainer from 'components/AuthModal/components/MotionContainer'
import InputProfileImages from 'components/AuthModal/components/InputProfileImages'
import InputGender from 'components/AuthModal/components/InputGender'
import InputPreferences from 'components/AuthModal/components/InputPreferences'
import InputSexuality from 'components/AuthModal/components/InputSexuality'
import InputOccupation from 'components/AuthModal/components/InputOccupation'
import InputEducation from 'components/AuthModal/components/InputEducation'
import InputLocation from 'components/AuthModal/components/InputLocation'
import ProgressBar from 'components/AuthModal/components/ProgressBar'

const STEPS_COLLECTIONS = Object.values(STEPS)

const checkLocationValidity = (e, userData) => {
  const { location, geoLat, geoLng } = userData
  const signupForm = e.target

  let locationError = ''
  if (!location || !geoLat || !geoLng) {
    locationError = 'No location selected'
  }
  signupForm.elements['location'].setCustomValidity(locationError)

  return form.checkValidity(e)
}

const Input = props => <DefaultInput classNames={{ input: 'border-white' }} {...props} />

const ProfileStep = ({ onClose, onSubmit, autoFocus, signupStep }) => {
  const { update } = useUpdateUser()
  const submitButtonRef = useRef()

  const { user: loggedInUser } = useAuthState()
  const [currentStep, setCurrentStep] = useState(STEPS.USERNAME)
  const [userData, setUserData] = useState({
    ...loggedInUser,
    /*
    preferences:
      loggedInUser?.preference.genders
        ?.map(
          ({ preference }) => PREFERENCE_OPTIONS.find(({ value }) => value === preference)?.value
        )
        .filter(preference => preference) || [],
     */
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const age = useMemo(
    () => dateFormatter.getAgeFromBirthdate(userData.birthdate),
    [userData.birthdate]
  )

  const { currentStepIndex, isLastStep, isFirstStep, progress } = useMemo(() => {
    const currentStepIndex = STEPS_COLLECTIONS.findIndex(step => step === currentStep)

    return {
      currentStepIndex,
      isLastStep: currentStepIndex === STEPS_COLLECTIONS.length - 1,
      isFirstStep: currentStepIndex === 0 || signupStep === currentStep,
      progress: ((currentStepIndex + 1) / STEPS_COLLECTIONS.length) * 100,
    }
  }, [currentStep, signupStep])

  const submitButton = useMemo(() => {
    let isDisabled

    switch (currentStep) {
      case STEPS.IMAGES: {
        isDisabled = userData.images.filter(({ url }) => url).length < MIN_NUM_OF_IMAGES
        break
      }
      case STEPS.GENDER: {
        isDisabled = !userData.gender || userData.gender === OTHER_GENDER
        break
      }
      case STEPS.GENDER_PREFERENCES: {
        const selectedPreferences = userData.preference.genders.filter(p => p)
        isDisabled = selectedPreferences.length === 0
        break
      }
      case STEPS.EDUCATION: {
        isDisabled = false
        break
      }
      default: {
        isDisabled = !get(userData, currentStep)
      }
    }

    const text = isLastStep ? 'Done' : 'Continue'

    return { isDisabled, text }
  }, [currentStep, isLastStep, userData])

  const goToStep = useCallback(
    direction => {
      const nextStepIndex = currentStepIndex + direction
      setCurrentStep(STEPS_COLLECTIONS[nextStepIndex])
    },
    [currentStepIndex]
  )

  const closeButton = useMemo(() => {
    const isVisible = true
    const Icon = isFirstStep ? TimesThinIcon : undefined

    return {
      isVisible,
      Icon,
      onClose: isFirstStep ? onClose : () => goToStep(-1),
    }
  }, [onClose, goToStep, isFirstStep])

  useEffect(() => {
    analytics.segmentTrack(`User Is In Profile: ${strings.capitalizeFirstLetter(currentStep)} Step`)
  }, [currentStep])

  const updateUser = useCallback(async () => {
    try {
      setIsSubmitting(true)
      const data = objectDiff(userData, loggedInUser)
      await update(data)
      onSubmit && onSubmit(true)
    } catch (error) {
      message.error(error.message || 'Please try again')
    } finally {
      setIsSubmitting(false)
    }
  }, [userData, loggedInUser, update, onSubmit])

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()

      if (!form.checkValidity(e)) {
        return
      }

      if (currentStep === STEPS.LOCATION && !checkLocationValidity(e, userData)) {
        return
      }

      if (isLastStep) {
        await updateUser()
        return
      }

      goToStep(1)
    },
    [currentStep, isLastStep, goToStep, updateUser, userData]
  )

  const handleChange = useCallback(
    e =>
      setUserData({
        ...userData,
        [e.target.name]: e.target.value,
      }),
    [userData]
  )

  const handleProfileChange = useCallback(
    e =>
      setUserData({
        ...userData,
        profile: {
          ...userData.profile,
          [e.target.name]: e.target.value,
        },
      }),
    [userData]
  )

  const handlePreferencesChange = useCallback(
    e =>
      setUserData({
        ...userData,
        preference: {
          ...userData.preference,
          [e.target.name]: e.target.value,
        },
      }),
    [userData]
  )

  const renderSteps = useMemo(() => {
    switch (currentStep) {
      case STEPS.USERNAME: {
        return (
          <MotionContainer key={currentStep}>
            <Label>Username</Label>
            <UsernameInput
              key={autoFocus}
              type="text"
              name="username"
              Input={Input}
              autoFocus={autoFocus}
              value={userData.username}
              defaultValue={userData.username}
              placeholder="Username"
              required
              onChange={handleChange}
            />
            <Description>Choose your unique username.</Description>
          </MotionContainer>
        )
      }
      case STEPS.BIRTHDATE: {
        return (
          <MotionContainer key={currentStep}>
            <Label>Birthday</Label>
            <BirthdateInput
              Input={Input}
              autoFocus={autoFocus}
              placeholder={BIRTHDATE_FORMAT.toUpperCase()}
              name="birthdate"
              value={strings.valueOrEmptyString(userData.birthdate)}
              onChange={handleChange}
              required
              classNames={{ errorMessage: 'right-16 block' }}
              styles={{
                input: {
                  display: 'block',
                  // ios shrinks the heights of date inputs that are empty
                  minHeight: '3.5rem',
                },
              }}
            />
            {age && (
              <Description>
                <u>{age}</u> years old
              </Description>
            )}
          </MotionContainer>
        )
      }
      case STEPS.SEXUAL_ORIENTATION: {
        return (
          <MotionContainer key={currentStep}>
            <InputSexuality onChange={handleChange} userData={userData} />
          </MotionContainer>
        )
      }
      case STEPS.GENDER: {
        return (
          <MotionContainer key={currentStep}>
            <InputGender onChange={handleChange} userData={userData} />
          </MotionContainer>
        )
      }
      case STEPS.GENDER_PREFERENCES: {
        return (
          <MotionContainer key={currentStep}>
            <InputPreferences onChange={handlePreferencesChange} userData={userData} />
          </MotionContainer>
        )
      }
      case STEPS.OCCUPATION: {
        return (
          <MotionContainer key={currentStep}>
            <InputOccupation onChange={handleProfileChange} userData={userData} />
          </MotionContainer>
        )
      }
      case STEPS.EDUCATION: {
        return (
          <MotionContainer key={currentStep}>
            <InputEducation onChange={handleProfileChange} userData={userData} />
          </MotionContainer>
        )
      }
      case STEPS.LOCATION: {
        const handleAddressChange = addressData => {
          setUserData(data => ({
            ...data,
            userAddress: {
              ...(data.userAddress || {}),
              ...addressData,
            },
          }))
        }

        return (
          <MotionContainer key={currentStep}>
            <InputLocation
              userData={userData}
              onLocationChange={locationData =>
                setUserData(data => ({
                  ...data,
                  ...locationData,
                }))
              }
              onAddressChange={handleAddressChange}
            />
          </MotionContainer>
        )
      }
      case STEPS.IMAGES: {
        return (
          <MotionContainer>
            <div>
              <Label>Photos</Label>
              <div className="-mt-10 text-white mb-10 opacity-60 text-base">
                Profile made public upon acceptance
              </div>
              <InputProfileImages onChange={handleChange} user={userData} />
            </div>
          </MotionContainer>
        )
      }
      default:
        return null
    }
  }, [
    currentStep,
    handleChange,
    handleProfileChange,
    handlePreferencesChange,
    userData,
    age,
    autoFocus,
  ])

  return (
    <>
      <form
        noValidate
        onSubmit={handleSubmit}
        className={classnames(PROFILE_CONTAINER, 'justify-between')}
      >
        {closeButton.isVisible && (
          <CloseButton onClose={closeButton.onClose} Icon={closeButton.Icon} />
        )}
        <div className="w-full">
          <ProgressBar progress={progress} />
          {renderSteps}
        </div>
        <div className="flex flex-col w-full items-center">
          <Button
            type="submit"
            className="order-1 w-full mt-6 mx-auto text-base sm:order-none"
            disabled={submitButton.isDisabled}
            loading={isSubmitting}
            ref={submitButtonRef}
          >
            {submitButton.text}
          </Button>
        </div>
      </form>
    </>
  )
}

ProfileStep.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  autoFocus: PropTypes.bool,
  user: PropTypes.object,
  event: PropTypes.object,
}

ProfileStep.defaultProps = {
  autoFocus: false,
}

export default ProfileStep
